import React, { useContext, useState } from 'react';
import { Form, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import { LocaleContext } from 'routes/Router';
import { Avatar, IconButton, Menu, MenuItem } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import { useConfirm } from 'material-ui-confirm';
import { logoutCallAPI } from 'store/logout/action';
import { providerAdminProfile } from 'routes/AppUrls';
import { ClearTokenRedirectLogin } from 'utils/general';
import ToastNotification from 'components/common/Toast';
import langIconEn from 'assets/images/navbar/arabicLangIcon.png';
import langIconAr from 'assets/images/navbar/englishLangIcon.png';
import 'components/navbar/navbar.scss'; // adjust path as needed
import Ptext from 'components/common/Ptext';

// ─── Component ─────────────────────────────────────────────────────────────────
const CustomNavbar = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const confirm = useConfirm();
  const [conf, setConf] = ToastNotification();
  const intl = useIntl();
  const { locale, setLocale } = useContext(LocaleContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const profileData = useSelector((state) => state?.userMeReducer?.data?.data);

  const pageTitle = useSelector((state) => state?.pageTitleReducer?.title);

  const LanguageSwitcher = (value) => {
    setLocale(value);
    const newUrl = window.location.pathname.replace(/^\/(en|ar)/, `/${value}`);
    window.location.href = newUrl;
  };

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleConfirmLogOut = () => {
    confirm({
      title: intl.formatMessage({ id: 'Confirm Logout' }),
      description: intl.formatMessage({
        id: 'Are you sure you want to Logout?',
      }),
      confirmationText: intl.formatMessage({ id: 'Logout' }),
      cancellationText: intl.formatMessage({ id: 'Cancel' }),
      dialogProps: { maxWidth: 'xs' },
      confirmationButtonProps: {
        sx: {
          bgcolor: '#1a1a1a',
          color: '#FFFFFF',
          borderRadius: '8px',
          '&:hover': { bgcolor: '#333' },
        },
      },
      cancellationButtonProps: {
        sx: {
          bgcolor: 'transparent',
          color: '#1a1a1a',
          borderRadius: '8px',
          border: '1px solid #1a1a1a',
        },
      },
    })
      .then(() => handleLogout())
      .catch(() => {});
  };

  const handleLogout = () => {
    dispatch(
      logoutCallAPI(
        {},
        { run: true, handleFunction: (res) => handleLogoutRedirect(res) }
      )
    );
  };

  const handleLogoutRedirect = (res) => {
    if (res?.data?.status_code === 200) {
      setConf({
        msg: intl.formatMessage({ id: 'logout successfully!' }),
        variant: 'success',
      });
      ClearTokenRedirectLogin(navigate);
    } else {
      setConf({
        msg: intl.formatMessage({ id: 'Please try again.' }),
        variant: 'error',
      });
    }
  };

  return (
    <header
      className={`custom-navbar ${locale === 'en' ? 'navbarEn' : 'navbarAr'}`}
    >
      {/* ── Left: page title ───────────────────────────── */}
      <Ptext variant="heading" className="navbar-page-title">
        <FormattedMessage id={pageTitle} />
      </Ptext>

      {/* ── Right: actions ─────────────────────────────── */}
      <div className="navbar-actions">
        {/* Language switcher */}
        <button
          className="navbar-lang-btn"
          onClick={() =>
            locale === 'en' ? LanguageSwitcher('ar') : LanguageSwitcher('en')
          }
        >
          <img
            src={locale === 'en' ? langIconAr : langIconEn}
            alt="Language"
            width="32"
            height="32"
          />
        </button>

        {/* Profile avatar */}
        <div className="navbar-avatar-wrapper" onClick={handleMenuOpen}>
          <IconButton style={{ padding: 0 }}>
            {profileData?.provider_details?.profile_photo?.file_url ? (
              <Avatar
                src={profileData.provider_details.profile_photo.file_url}
                sx={{ width: 36, height: 36 }}
              />
            ) : (
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: '#c5e8c5',
                  color: '#2d6a2d',
                  fontSize: '13px',
                  fontWeight: 700,
                }}
              >
                {profileData?.email?.charAt(0)?.toUpperCase()}
              </Avatar>
            )}
          </IconButton>
          <KeyboardArrowDownIcon
            style={{ color: '#6b7280', fontSize: '18px' }}
          />
        </div>
      </div>

      {/* ── Dropdown menu — matches sidebar style ──────── */}
      <Menu
        open={open}
        onClose={handleMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={{
          top: 60,
          left: locale === 'en' ? window.innerWidth - 10 : 10,
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: locale === 'en' ? 'right' : 'left',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: '12px',
            width: 'auto',
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            overflow: 'hidden',
          },
        }}
        sx={{ zIndex: 2200 }}
      >
        {/* "Hi, email" header — email truncated to 22 chars */}
        <MenuItem divider>
          <span className="fs-14 fw-400 fc-404040 ff-Akkurat">
            Hi,{' '}
            {profileData?.email?.length > 22
              ? `${profileData.email.slice(0, 22)}…`
              : profileData?.email}
          </span>
        </MenuItem>

        <MenuItem
          onClick={() => {
            navigate(providerAdminProfile(locale));
            handleMenuClose();
          }}
          className="flex items-left gap-2 mt-2"
        >
          <PersonIcon className="fs-14 fw-400 fc-404040 ff-Akkurat" />
          <span className="fs-14 fw-400 fc-404040 ff-Akkurat">
            <FormattedMessage id="My Profile" />
          </span>
        </MenuItem>

        <MenuItem
          onClick={() => {
            handleConfirmLogOut();
            handleMenuClose();
          }}
          className="flex items-left gap-2"
        >
          <LogoutIcon className="fs-14 fw-400 fc-404040 ff-Akkurat" />
          <span className="fs-14 fw-400 fc-404040 ff-Akkurat">
            <FormattedMessage id="Logout" />
          </span>
        </MenuItem>
      </Menu>
    </header>
  );
};

export default CustomNavbar;
